/** The four widths every Pigment preview must prove before publication. */
export const PIGMENT_BROWSER_VIEWPORTS = [
    { name: "desktop", width: 1440, height: 1000, deviceScaleFactor: 1 },
    { name: "tablet", width: 768, height: 1024, deviceScaleFactor: 1 },
    { name: "mobile", width: 390, height: 844, deviceScaleFactor: 1 },
    { name: "narrow", width: 320, height: 568, deviceScaleFactor: 1 },
];
/** Creates the canonical four-viewport request and rejects ambiguous lineage up front. */
export function createPigmentBrowserInspectionJob(input) {
    const visibilityPolicy = input.expectedVisibleBlockIdsByViewport;
    const job = {
        ...input,
        schema: "pigment.browser-inspection-job.v2",
        expectedBlockIds: [...input.expectedBlockIds],
        ...(visibilityPolicy !== undefined ? {
            expectedVisibleBlockIdsByViewport: cloneVisibilityPolicy(visibilityPolicy),
        } : {}),
        viewports: PIGMENT_BROWSER_VIEWPORTS.map((viewport) => ({ ...viewport })),
    };
    const issues = validatePigmentBrowserInspectionJob(job);
    if (issues.length > 0)
        throw new PigmentBrowserInspectionContractError("Invalid Pigment browser inspection job.", issues);
    return job;
}
function cloneVisibilityPolicy(value) {
    if (!isRecord(value))
        return value;
    return Object.fromEntries(Object.entries(value).map(([name, ids]) => [name, Array.isArray(ids) ? [...ids] : ids]));
}
export function validatePigmentBrowserInspectionJob(value) {
    const issues = [];
    if (!isRecord(value))
        return [contract("invalid_job", "$", "Inspection job must be an object.")];
    if (value.schema !== "pigment.browser-inspection-job.v2")
        pushContract(issues, "invalid_schema", "$.schema", "Unsupported inspection job schema.");
    textField(value.jobId, "$.jobId", issues);
    timestampField(value.requestedAt, "$.requestedAt", issues);
    previewUrlField(value.previewUrl, "$.previewUrl", issues);
    validateLineage(value.lineage, "$.lineage", issues);
    const blockIds = stringArray(value.expectedBlockIds, "$.expectedBlockIds", issues);
    if (blockIds && new Set(blockIds).size !== blockIds.length)
        pushContract(issues, "duplicate_block_id", "$.expectedBlockIds", "Expected block IDs must be unique.");
    validateExpectedVisibility(value.expectedVisibleBlockIdsByViewport, blockIds, "$.expectedVisibleBlockIdsByViewport", issues);
    validateViewports(value.viewports, "$.viewports", issues);
    return issues;
}
export function assertPigmentBrowserInspectionJob(value) {
    const issues = validatePigmentBrowserInspectionJob(value);
    if (issues.length > 0)
        throw new PigmentBrowserInspectionContractError("Invalid Pigment browser inspection job.", issues);
}
/**
 * Validates shape, exact job lineage, evidence completeness, and acceptance.
 * A failed report with complete adverse evidence is valid but not accepted.
 */
export function validatePigmentBrowserInspectionReport(value, expectedJob) {
    const issues = [];
    if (!isRecord(value))
        return { valid: false, accepted: false, issues: [contract("invalid_report", "$", "Inspection report must be an object.")] };
    if (value.schema !== "pigment.browser-inspection-report.v2")
        pushContract(issues, "invalid_schema", "$.schema", "Unsupported inspection report schema.");
    textField(value.reportId, "$.reportId", issues);
    const jobIssues = validatePigmentBrowserInspectionJob(value.job);
    for (const issue of jobIssues)
        issues.push({ ...issue, path: `$.job${issue.path.slice(1)}` });
    if (expectedJob && !sameJson(value.job, expectedJob))
        pushContract(issues, "job_mismatch", "$.job", "Report does not bind the exact submitted inspection job.");
    validateExecutor(value.executor, "$.executor", issues);
    timestampField(value.startedAt, "$.startedAt", issues);
    timestampField(value.completedAt, "$.completedAt", issues);
    if (isTimestamp(value.startedAt) && isTimestamp(value.completedAt) && Date.parse(value.completedAt) < Date.parse(value.startedAt)) {
        pushContract(issues, "invalid_time_order", "$.completedAt", "Report completion precedes its start.");
    }
    if (value.outcome !== "passed" && value.outcome !== "failed")
        pushContract(issues, "invalid_outcome", "$.outcome", "Outcome must be passed or failed.");
    const evidence = Array.isArray(value.evidence) ? value.evidence : null;
    if (!evidence) {
        pushContract(issues, "invalid_evidence", "$.evidence", "Viewport evidence must be an array.");
    }
    else if (isRecord(value.job)) {
        validateEvidenceSet(evidence, value.job, issues);
    }
    const contractIssuesBeforeOutcome = issues.filter((issue) => issue.kind === "contract").length;
    const adverse = issues.some((issue) => issue.kind === "acceptance");
    if (contractIssuesBeforeOutcome === 0 && (value.outcome === "passed") !== !adverse) {
        pushContract(issues, "outcome_mismatch", "$.outcome", "Outcome does not match the captured acceptance evidence.");
    }
    const valid = !issues.some((issue) => issue.kind === "contract");
    return { valid, accepted: valid && !adverse, issues };
}
/**
 * Derives the only valid report outcome from canonical evidence. Executors use
 * this instead of maintaining a second, drifting interpretation of acceptance.
 */
export function derivePigmentBrowserInspectionOutcome(evidence, job) {
    const issues = validatePigmentBrowserInspectionJob(job);
    if (issues.length > 0)
        throw new PigmentBrowserInspectionContractError("Invalid Pigment browser inspection job.", issues);
    validateEvidenceSet(evidence, job, issues);
    if (issues.some((issue) => issue.kind === "contract")) {
        throw new PigmentBrowserInspectionContractError("Invalid Pigment browser inspection evidence.", issues);
    }
    return issues.some((issue) => issue.kind === "acceptance") ? "failed" : "passed";
}
export function assertPigmentBrowserInspectionReport(value, expectedJob) {
    const validation = validatePigmentBrowserInspectionReport(value, expectedJob);
    if (!validation.valid)
        throw new PigmentBrowserInspectionContractError("Invalid Pigment browser inspection report.", validation.issues);
}
export class PigmentBrowserInspectionContractError extends Error {
    issues;
    constructor(message, issues) {
        super(message);
        this.name = "PigmentBrowserInspectionContractError";
        this.issues = issues;
        Object.setPrototypeOf(this, new.target.prototype);
    }
}
/**
 * Collects transport evidence and refuses results not bound to the job.
 *
 * The returned report is still untrusted. Only a host-authorized executor may
 * upload its screenshot bytes and submit the report for durable acceptance.
 */
export async function collectPigmentBrowserInspectionEvidence(transport, job) {
    const jobIssues = validatePigmentBrowserInspectionJob(job);
    if (jobIssues.length > 0)
        throw new PigmentBrowserInspectionContractError("Invalid Pigment browser inspection job.", jobIssues);
    const report = await transport.inspect(job);
    assertPigmentBrowserInspectionReport(report, job);
    return report;
}
function validateLineage(value, path, issues) {
    if (!isRecord(value))
        return pushContract(issues, "invalid_lineage", path, "Preview lineage must be an object.");
    textField(value.previewId, `${path}.previewId`, issues);
    if (value.parentPreviewId !== null)
        textField(value.parentPreviewId, `${path}.parentPreviewId`, issues);
    textField(value.page, `${path}.page`, issues);
    integerField(value.baseRevision, `${path}.baseRevision`, issues);
    integerField(value.candidateRevision, `${path}.candidateRevision`, issues);
    shaField(value.candidateHash, `${path}.candidateHash`, issues);
    shaField(value.mutationHash, `${path}.mutationHash`, issues);
    shaField(value.artifactHash, `${path}.artifactHash`, issues);
    if (isInteger(value.baseRevision) && isInteger(value.candidateRevision) && value.candidateRevision <= value.baseRevision) {
        pushContract(issues, "invalid_revision_lineage", `${path}.candidateRevision`, "Candidate revision must advance the base revision.");
    }
}
function validateViewports(value, path, issues) {
    if (!Array.isArray(value))
        return pushContract(issues, "invalid_viewports", path, "Viewports must be an array.");
    if (!sameJson(value, PIGMENT_BROWSER_VIEWPORTS))
        pushContract(issues, "viewport_set_mismatch", path, "Inspection must use the canonical 1440, 768, 390, and 320 viewport definitions.");
}
function validateExecutor(value, path, issues) {
    if (!isRecord(value))
        return pushContract(issues, "invalid_executor", path, "Executor identity must be an object.");
    for (const key of ["name", "version", "browserName", "browserVersion"])
        textField(value[key], `${path}.${key}`, issues);
}
function validateEvidenceSet(evidence, job, issues) {
    const jobViewports = Array.isArray(job.viewports) ? job.viewports : [];
    if (evidence.length !== jobViewports.length)
        pushContract(issues, "viewport_evidence_count", "$.evidence", "Report must contain exactly one entry for every requested viewport.");
    for (let index = 0; index < jobViewports.length; index += 1) {
        const expected = jobViewports[index];
        const hitIndex = evidence.findIndex((item) => isRecord(item) && sameJson(item.viewport, expected));
        if (hitIndex < 0) {
            pushContract(issues, "missing_viewport_evidence", "$.evidence", `Missing evidence for viewport ${viewportLabel(expected)}.`);
            continue;
        }
        const viewportName = isRecord(expected) && typeof expected.name === "string" ? expected.name : "";
        validateViewportEvidence(evidence[hitIndex], expected, job, expectedVisibleBlockIds(job, viewportName), `$.evidence[${hitIndex}]`, issues);
    }
    const signatures = evidence.map((item) => isRecord(item) ? JSON.stringify(item.viewport) : "invalid");
    if (new Set(signatures).size !== signatures.length)
        pushContract(issues, "duplicate_viewport_evidence", "$.evidence", "Viewport evidence entries must be unique.");
}
function validateViewportEvidence(value, expectedViewport, job, expectedVisibleBlockIdsValue, path, issues) {
    if (!isRecord(value))
        return pushContract(issues, "invalid_viewport_evidence", path, "Viewport evidence must be an object.");
    if (!sameJson(value.viewport, expectedViewport))
        pushContract(issues, "viewport_mismatch", `${path}.viewport`, "Captured viewport does not match its request.");
    timestampField(value.capturedAt, `${path}.capturedAt`, issues);
    previewUrlField(value.finalUrl, `${path}.finalUrl`, issues);
    if (typeof value.finalUrl === "string" && typeof job.previewUrl === "string" && value.finalUrl !== job.previewUrl) {
        acceptance(issues, "preview_url_changed", `${path}.finalUrl`, "Browser did not finish on the exact preview URL.");
    }
    validateScreenshot(value.screenshot, expectedViewport, `${path}.screenshot`, issues);
    validateAxe(value.axe, `${path}.axe`, issues);
    validateConsole(value.console, `${path}.console`, issues);
    validateNetwork(value.network, `${path}.network`, issues);
    validateOverflow(value.horizontalOverflow, `${path}.horizontalOverflow`, issues);
    validateBlocks(value.blocks, job.expectedBlockIds, expectedVisibleBlockIdsValue, `${path}.blocks`, issues);
}
function validateExpectedVisibility(value, expectedBlockIds, path, issues) {
    if (value === undefined)
        return;
    if (!isRecord(value))
        return pushContract(issues, "invalid_visibility_policy", path, "Responsive block visibility policy must be an object.");
    const viewportNames = PIGMENT_BROWSER_VIEWPORTS.map(({ name }) => name);
    if (!sameStringSet(Object.keys(value), viewportNames)) {
        pushContract(issues, "visibility_viewport_mismatch", path, "Responsive block visibility policy must cover every canonical viewport exactly once.");
    }
    const expected = new Set(expectedBlockIds ?? []);
    const visibleSomewhere = new Set();
    for (const viewportName of viewportNames) {
        const ids = stringArray(value[viewportName], `${path}.${viewportName}`, issues);
        if (!ids)
            continue;
        if (new Set(ids).size !== ids.length)
            pushContract(issues, "duplicate_visible_block_id", `${path}.${viewportName}`, "Visible block IDs must be unique per viewport.");
        for (const id of ids) {
            if (!expected.has(id))
                pushContract(issues, "unknown_visible_block_id", `${path}.${viewportName}`, `Visible block ${id} is not an expected block.`);
            visibleSomewhere.add(id);
        }
    }
    if (expectedBlockIds && !sameStringSet([...visibleSomewhere], expectedBlockIds)) {
        pushContract(issues, "block_never_expected_visible", path, "Every expected block must be visible in at least one canonical viewport.");
    }
}
function expectedVisibleBlockIds(job, viewportName) {
    if (!isRecord(job.expectedVisibleBlockIdsByViewport))
        return job.expectedBlockIds;
    return job.expectedVisibleBlockIdsByViewport[viewportName];
}
function validateScreenshot(value, viewport, path, issues) {
    if (!isRecord(value))
        return pushContract(issues, "invalid_screenshot", path, "Screenshot evidence must be an object.");
    urlField(value.uri, `${path}.uri`, issues, true);
    if (value.mediaType !== "image/png")
        pushContract(issues, "invalid_media_type", `${path}.mediaType`, "Screenshots must be PNG.");
    shaField(value.sha256, `${path}.sha256`, issues);
    positiveIntegerField(value.pixelWidth, `${path}.pixelWidth`, issues);
    positiveIntegerField(value.pixelHeight, `${path}.pixelHeight`, issues);
    if (isRecord(viewport) && value.pixelWidth !== viewport.width)
        pushContract(issues, "screenshot_width_mismatch", `${path}.pixelWidth`, "Screenshot width must equal viewport width at device scale 1.");
    if (isRecord(viewport) && typeof viewport.height === "number" && typeof value.pixelHeight === "number" && value.pixelHeight < viewport.height)
        pushContract(issues, "screenshot_height_mismatch", `${path}.pixelHeight`, "Screenshot must capture at least the full viewport height.");
}
function validateAxe(value, path, issues) {
    if (!isRecord(value))
        return pushContract(issues, "invalid_axe", path, "Axe evidence must be an object.");
    if (value.tool !== "axe-core")
        pushContract(issues, "invalid_axe_tool", `${path}.tool`, "Accessibility evidence must identify axe-core.");
    textField(value.version, `${path}.version`, issues);
    timestampField(value.testedAt, `${path}.testedAt`, issues);
    const violations = findingArray(value.violations, `${path}.violations`, issues);
    const incomplete = findingArray(value.incomplete, `${path}.incomplete`, issues);
    nonNegativeIntegerField(value.passes, `${path}.passes`, issues);
    nonNegativeIntegerField(value.inapplicable, `${path}.inapplicable`, issues);
    if (violations && violations.length > 0)
        acceptance(issues, "axe_violation", `${path}.violations`, `Axe reported ${violations.length} violation(s).`);
    if (incomplete && incomplete.length > 0)
        acceptance(issues, "axe_incomplete", `${path}.incomplete`, `Axe reported ${incomplete.length} incomplete check(s).`);
}
function findingArray(value, path, issues) {
    if (!Array.isArray(value)) {
        pushContract(issues, "invalid_axe_findings", path, "Axe findings must be an array.");
        return null;
    }
    value.forEach((finding, index) => {
        const at = `${path}[${index}]`;
        if (!isRecord(finding))
            return pushContract(issues, "invalid_axe_finding", at, "Axe finding must be an object.");
        for (const key of ["id", "description", "help"])
            textField(finding[key], `${at}.${key}`, issues);
        urlField(finding.helpUrl, `${at}.helpUrl`, issues);
        if (finding.impact !== null && !["minor", "moderate", "serious", "critical"].includes(String(finding.impact)))
            pushContract(issues, "invalid_axe_impact", `${at}.impact`, "Invalid Axe impact.");
        if (!Array.isArray(finding.nodes) || finding.nodes.length === 0) {
            pushContract(issues, "invalid_axe_nodes", `${at}.nodes`, "Axe finding must include affected nodes.");
        }
        else {
            finding.nodes.forEach((node, nodeIndex) => {
                const nodeAt = `${at}.nodes[${nodeIndex}]`;
                if (!isRecord(node))
                    return pushContract(issues, "invalid_axe_node", nodeAt, "Axe node must be an object.");
                const target = stringArray(node.target, `${nodeAt}.target`, issues);
                if (target?.length === 0)
                    pushContract(issues, "invalid_axe_target", `${nodeAt}.target`, "Axe node must include a selector target.");
                textField(node.html, `${nodeAt}.html`, issues);
                if (node.failureSummary !== null && (typeof node.failureSummary !== "string" || !node.failureSummary.trim()))
                    pushContract(issues, "invalid_failure_summary", `${nodeAt}.failureSummary`, "Failure summary must be non-empty or null.");
            });
        }
    });
    return value;
}
function validateConsole(value, path, issues) {
    if (!Array.isArray(value))
        return pushContract(issues, "invalid_console", path, "Console evidence must be an array.");
    value.forEach((entry, index) => {
        const at = `${path}[${index}]`;
        if (!isRecord(entry))
            return pushContract(issues, "invalid_console_entry", at, "Console entry must be an object.");
        timestampField(entry.timestamp, `${at}.timestamp`, issues);
        if (!["debug", "info", "warning", "error"].includes(String(entry.level)))
            pushContract(issues, "invalid_console_level", `${at}.level`, "Invalid console level.");
        textField(entry.source, `${at}.source`, issues);
        textField(entry.text, `${at}.text`, issues);
        nullableUrlField(entry.url, `${at}.url`, issues);
        nullableLocationField(entry.line, `${at}.line`, issues);
        nullableLocationField(entry.column, `${at}.column`, issues);
        if (entry.level === "error")
            acceptance(issues, "console_error", at, `Browser console error: ${String(entry.text)}`);
    });
}
function validateNetwork(value, path, issues) {
    if (!Array.isArray(value))
        return pushContract(issues, "invalid_network", path, "Network evidence must be an array.");
    value.forEach((entry, index) => {
        const at = `${path}[${index}]`;
        if (!isRecord(entry))
            return pushContract(issues, "invalid_network_entry", at, "Network entry must be an object.");
        timestampField(entry.startedAt, `${at}.startedAt`, issues);
        timestampField(entry.completedAt, `${at}.completedAt`, issues);
        if (isTimestamp(entry.startedAt) && isTimestamp(entry.completedAt) && Date.parse(entry.completedAt) < Date.parse(entry.startedAt))
            pushContract(issues, "invalid_network_time_order", `${at}.completedAt`, "Network completion precedes its start.");
        textField(entry.method, `${at}.method`, issues);
        urlField(entry.url, `${at}.url`, issues);
        textField(entry.resourceType, `${at}.resourceType`, issues);
        if (entry.status !== null && (!isInteger(entry.status) || entry.status < 100 || entry.status > 599))
            pushContract(issues, "invalid_network_status", `${at}.status`, "Network status must be an HTTP status or null.");
        if (entry.failure !== null && (typeof entry.failure !== "string" || !entry.failure.trim()))
            pushContract(issues, "invalid_network_failure", `${at}.failure`, "Network failure must be non-empty or null.");
        if (typeof entry.failure === "string")
            acceptance(issues, "network_failure", at, `Network request failed: ${entry.failure}`);
        if (typeof entry.status === "number" && entry.status >= 400)
            acceptance(issues, "network_http_error", at, `Network request returned HTTP ${entry.status}.`);
    });
}
function validateOverflow(value, path, issues) {
    if (!isRecord(value))
        return pushContract(issues, "invalid_overflow", path, "Overflow evidence must be an object.");
    if (typeof value.detected !== "boolean")
        pushContract(issues, "invalid_overflow_flag", `${path}.detected`, "Overflow detected must be boolean.");
    nonNegativeIntegerField(value.documentClientWidth, `${path}.documentClientWidth`, issues);
    nonNegativeIntegerField(value.documentScrollWidth, `${path}.documentScrollWidth`, issues);
    if (!Array.isArray(value.offenders)) {
        pushContract(issues, "invalid_overflow_offenders", `${path}.offenders`, "Overflow offenders must be an array.");
    }
    else {
        value.offenders.forEach((offender, index) => {
            const at = `${path}.offenders[${index}]`;
            if (!isRecord(offender))
                return pushContract(issues, "invalid_overflow_offender", at, "Overflow offender must be an object.");
            textField(offender.selector, `${at}.selector`, issues);
            validateRect(offender.rect, `${at}.rect`, issues);
            nonNegativeIntegerField(offender.clientWidth, `${at}.clientWidth`, issues);
            nonNegativeIntegerField(offender.scrollWidth, `${at}.scrollWidth`, issues);
        });
    }
    if (isInteger(value.documentClientWidth) && isInteger(value.documentScrollWidth) && (value.documentScrollWidth > value.documentClientWidth) !== (value.detected === true))
        pushContract(issues, "overflow_dimensions_mismatch", path, "Overflow flag must agree with document width evidence.");
    if (value.detected === true)
        acceptance(issues, "horizontal_overflow", path, "Horizontal overflow was detected.");
    if (value.detected === false && Array.isArray(value.offenders) && value.offenders.length > 0)
        pushContract(issues, "overflow_evidence_mismatch", `${path}.offenders`, "Overflow offenders exist while detected is false.");
}
function validateBlocks(value, expectedValue, expectedVisibleValue, path, issues) {
    if (!Array.isArray(value))
        return pushContract(issues, "invalid_block_map", path, "Block-to-DOM evidence must be an array.");
    const expected = Array.isArray(expectedValue) ? expectedValue.filter((item) => typeof item === "string") : [];
    const expectedVisible = new Set(Array.isArray(expectedVisibleValue)
        ? expectedVisibleValue.filter((item) => typeof item === "string")
        : []);
    const ids = value.map((item) => isRecord(item) ? item.blockId : undefined);
    if (!sameStringSet(ids, expected))
        pushContract(issues, "block_map_mismatch", path, "Block map must contain every expected block ID exactly once.");
    value.forEach((mapping, index) => {
        const at = `${path}[${index}]`;
        if (!isRecord(mapping))
            return pushContract(issues, "invalid_block_mapping", at, "Block mapping must be an object.");
        textField(mapping.blockId, `${at}.blockId`, issues);
        if (!["mapped", "missing", "ambiguous"].includes(String(mapping.status)))
            pushContract(issues, "invalid_block_status", `${at}.status`, "Invalid block mapping status.");
        if (!Array.isArray(mapping.matches))
            pushContract(issues, "invalid_block_matches", `${at}.matches`, "DOM matches must be an array.");
        if (Array.isArray(mapping.matches))
            mapping.matches.forEach((match, matchIndex) => {
                const matchAt = `${at}.matches[${matchIndex}]`;
                if (!isRecord(match))
                    return pushContract(issues, "invalid_dom_match", matchAt, "DOM match must be an object.");
                textField(match.selector, `${matchAt}.selector`, issues);
                textField(match.tagName, `${matchAt}.tagName`, issues);
                validateRect(match.rect, `${matchAt}.rect`, issues);
                if (typeof match.visible !== "boolean")
                    pushContract(issues, "invalid_visibility", `${matchAt}.visible`, "DOM visibility must be boolean.");
            });
        const count = Array.isArray(mapping.matches) ? mapping.matches.length : -1;
        if ((mapping.status === "mapped" && count !== 1) || (mapping.status === "missing" && count !== 0) || (mapping.status === "ambiguous" && count < 2)) {
            pushContract(issues, "block_status_mismatch", at, "Block status does not match its DOM match count.");
        }
        const mappedMatch = mapping.status === "mapped" && Array.isArray(mapping.matches) && isRecord(mapping.matches[0]) ? mapping.matches[0] : null;
        const hasVisibleArea = Boolean(mappedMatch && mappedMatch.visible === true && isRecord(mappedMatch.rect)
            && typeof mappedMatch.rect.width === "number" && mappedMatch.rect.width > 0
            && typeof mappedMatch.rect.height === "number" && mappedMatch.rect.height > 0);
        if (mappedMatch && expectedVisible.has(String(mapping.blockId)) && !hasVisibleArea) {
            acceptance(issues, "block_not_visible", at, `Pigment block ${String(mapping.blockId)} is expected to be visible but has no visible rendered area.`);
        }
        if (mappedMatch && !expectedVisible.has(String(mapping.blockId)) && hasVisibleArea) {
            acceptance(issues, "block_unexpectedly_visible", at, `Pigment block ${String(mapping.blockId)} is visible outside its host-issued responsive policy.`);
        }
        if (mapping.status === "missing")
            acceptance(issues, "block_missing", at, `Pigment block ${String(mapping.blockId)} is absent from the DOM.`);
        if (mapping.status === "ambiguous")
            acceptance(issues, "block_ambiguous", at, `Pigment block ${String(mapping.blockId)} maps to multiple DOM nodes.`);
    });
}
function contract(code, path, message) { return { kind: "contract", code, path, message }; }
function pushContract(issues, code, path, message) { issues.push(contract(code, path, message)); }
function acceptance(issues, code, path, message) { issues.push({ kind: "acceptance", code, path, message }); }
function isRecord(value) { return typeof value === "object" && value !== null && !Array.isArray(value); }
function isInteger(value) { return typeof value === "number" && Number.isSafeInteger(value) && value >= 0; }
function isTimestamp(value) { return typeof value === "string" && value.trim() === value && Number.isFinite(Date.parse(value)); }
function textField(value, path, issues) { if (typeof value !== "string" || !value.trim() || value !== value.trim())
    pushContract(issues, "invalid_text", path, "Value must be non-empty trimmed text."); }
function timestampField(value, path, issues) { if (!isTimestamp(value))
    pushContract(issues, "invalid_timestamp", path, "Value must be an ISO-compatible timestamp."); }
function integerField(value, path, issues) { if (!isInteger(value))
    pushContract(issues, "invalid_integer", path, "Value must be a non-negative safe integer."); }
function nonNegativeIntegerField(value, path, issues) { integerField(value, path, issues); }
function positiveIntegerField(value, path, issues) { if (!isInteger(value) || value === 0)
    pushContract(issues, "invalid_positive_integer", path, "Value must be a positive safe integer."); }
function shaField(value, path, issues) { if (typeof value !== "string" || !/^sha256:[a-f0-9]{64}$/.test(value))
    pushContract(issues, "invalid_sha256", path, "Value must be a lowercase sha256 digest."); }
function urlField(value, path, issues, allowArtifact = false) {
    if (typeof value !== "string" || !value.trim())
        return pushContract(issues, "invalid_url", path, "Value must be a URL.");
    try {
        const url = new URL(value);
        if (!allowArtifact && !["http:", "https:"].includes(url.protocol))
            throw new Error();
        if (allowArtifact && !["http:", "https:", "file:", "s3:"].includes(url.protocol))
            throw new Error();
        if (url.username || url.password)
            throw new Error();
    }
    catch {
        pushContract(issues, "invalid_url", path, "Value must use an allowed credential-free absolute URL.");
    }
}
function previewUrlField(value, path, issues) {
    const before = issues.length;
    urlField(value, path, issues);
    if (issues.length !== before || typeof value !== "string")
        return;
    const url = new URL(value);
    if (url.search || url.hash)
        pushContract(issues, "credential_bearing_preview_url", path, "Preview URLs must not contain query or fragment data; send authority in headers.");
}
function nullableUrlField(value, path, issues) { if (value !== null)
    urlField(value, path, issues); }
function nullableLocationField(value, path, issues) { if (value !== null && !isInteger(value))
    pushContract(issues, "invalid_location", path, "Source location must be a non-negative integer or null."); }
function validateRect(value, path, issues) {
    if (!isRecord(value))
        return pushContract(issues, "invalid_rect", path, "DOM rectangle must be an object.");
    for (const key of ["x", "y", "width", "height"])
        if (typeof value[key] !== "number" || !Number.isFinite(value[key]))
            pushContract(issues, "invalid_rect_value", `${path}.${key}`, "Rectangle coordinates must be finite numbers.");
    if (typeof value.width === "number" && value.width < 0)
        pushContract(issues, "invalid_rect_width", `${path}.width`, "Rectangle width cannot be negative.");
    if (typeof value.height === "number" && value.height < 0)
        pushContract(issues, "invalid_rect_height", `${path}.height`, "Rectangle height cannot be negative.");
}
function stringArray(value, path, issues) { if (!Array.isArray(value)) {
    pushContract(issues, "invalid_string_array", path, "Value must be an array.");
    return null;
} value.forEach((item, index) => textField(item, `${path}[${index}]`, issues)); return value.filter((item) => typeof item === "string"); }
function sameStringSet(actual, expected) { return actual.length === expected.length && actual.every((item) => typeof item === "string") && new Set(actual).size === actual.length && expected.every((item) => actual.includes(item)); }
function sameJson(left, right) {
    if (Object.is(left, right))
        return true;
    if (Array.isArray(left) || Array.isArray(right))
        return Array.isArray(left) && Array.isArray(right) && left.length === right.length && left.every((item, index) => sameJson(item, right[index]));
    if (!isRecord(left) || !isRecord(right))
        return false;
    const leftKeys = Object.keys(left).sort();
    const rightKeys = Object.keys(right).sort();
    return leftKeys.length === rightKeys.length && leftKeys.every((key, index) => key === rightKeys[index] && sameJson(left[key], right[key]));
}
function viewportLabel(value) { return isRecord(value) ? `${String(value.name)} (${String(value.width)}px)` : "unknown"; }
